import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { FiUpload, FiTrash2, FiDownload, FiFileText, FiEdit, FiX, FiCheck, FiShield, FiEye, FiBookOpen, FiClipboard, FiDollarSign } from 'react-icons/fi';
import { documentTemplatesAPI, settingsAPI } from '../../services/api';
import {
  getTemplateFieldInputKind,
  isoDateToDdMmYyyy,
  ddMmYyyyToIso,
  normalizeTemplateDateValueForIso
} from '../../utils/documentTemplateFields';
import Modal from './Modal';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

// ─── ADMIN TEMPLATE MANAGEMENT (Settings page) ───
export const DocumentTemplateSettings = () => {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'admin', 'manager'].includes(String(user?.role || '').toLowerCase());
  const isSuperAdmin = String(user?.role || '').toLowerCase() === 'super_admin';
  const { t } = useTranslation();
  const letterFeeGateOptions = useMemo(
    () => [
      { value: 'none', label: t('documentGenerator.feeGate.anyTime') },
      { value: 'application_fee', label: t('documentGenerator.feeGate.afterApplicationFee') },
      { value: 'tuition_fee', label: t('documentGenerator.feeGate.afterTuitionFee') }
    ],
    [t]
  );
  const letterTabAccessOptions = useMemo(
    () => [
      {
        value: 'all',
        label: t('documentGenerator.letterTab.allLabel'),
        hint: t('documentGenerator.letterTab.allHint')
      },
      {
        value: 'admin_superadmin',
        label: t('documentGenerator.letterTab.adminSuperadminLabel'),
        hint: t('documentGenerator.letterTab.adminSuperadminHint')
      },
      {
        value: 'superadmin_only',
        label: t('documentGenerator.letterTab.superadminOnlyLabel'),
        hint: t('documentGenerator.letterTab.superadminOnlyHint')
      },
      {
        value: 'specific_users',
        label: t('documentGenerator.letterTab.specificUsersLabel'),
        hint: t('documentGenerator.letterTab.specificUsersHint')
      }
    ],
    [t]
  );
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [uploadType, setUploadType] = useState('letter');
  const [uploadLetterCampusId, setUploadLetterCampusId] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceTpl, setReplaceTpl] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const [campuses, setCampuses] = useState([]);
  const [letterTabAccess, setLetterTabAccess] = useState({
    access_mode: 'all',
    specific_user_ids: []
  });
  const [letterTabAccessLoading, setLetterTabAccessLoading] = useState(true);
  const [letterTabSaving, setLetterTabSaving] = useState(false);
  const [allUsersForAccess, setAllUsersForAccess] = useState([]);

  const loadCampuses = useCallback(async () => {
    try {
      const res = await settingsAPI.getCampuses();
      setCampuses((res.data || []).filter((c) => c.status === 'active'));
    } catch {
      setCampuses([]);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await documentTemplatesAPI.list();
      setTemplates(res.data);
    } catch {
      toast.error(t('documentGenerator.toasts.loadTemplatesFailed'));
    }
    setLoading(false);
  }, [t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCampuses(); }, [loadCampuses]);

  useEffect(() => {
    if (!isSuperAdmin) {
      setLetterTabAccessLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [accRes, usersRes] = await Promise.all([
          settingsAPI.getLetterGenerationAccess(),
          settingsAPI.getUsers()
        ]);
        if (!cancelled) {
          setLetterTabAccess({
            access_mode: accRes.data?.access_mode || 'all',
            specific_user_ids: Array.isArray(accRes.data?.specific_user_ids)
              ? accRes.data.specific_user_ids.map(Number)
              : []
          });
          setAllUsersForAccess(
            (usersRes.data || []).filter((u) => String(u?.status || '').toLowerCase() !== 'inactive')
          );
        }
      } catch {
        if (!cancelled) toast.error(t('documentGenerator.toasts.loadLetterTabFailed'));
      } finally {
        if (!cancelled) setLetterTabAccessLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, t]);

  const saveLetterTabAccess = async () => {
    setLetterTabSaving(true);
    try {
      const res = await settingsAPI.updateLetterGenerationAccess({
        access_mode: letterTabAccess.access_mode,
        specific_user_ids:
          letterTabAccess.access_mode === 'specific_users' ? letterTabAccess.specific_user_ids : []
      });
      setLetterTabAccess({
        access_mode: res.data?.access_mode || 'all',
        specific_user_ids: Array.isArray(res.data?.specific_user_ids) ? res.data.specific_user_ids.map(Number) : []
      });
      toast.success(t('documentGenerator.toasts.letterTabSaved'));
    } catch (err) {
      toast.error(err.response?.data?.message || t('documentGenerator.toasts.saveFailed'));
    }
    setLetterTabSaving(false);
  };

  const toggleSpecificUser = (userId) => {
    const id = Number(userId);
    setLetterTabAccess((prev) => {
      const set = new Set(prev.specific_user_ids || []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, specific_user_ids: [...set] };
    });
  };

  const handleUpload = async () => {
    if (!uploadName.trim()) return toast.error(t('documentGenerator.toasts.templateNameRequired'));
    if (!uploadFile) return toast.error(t('documentGenerator.toasts.selectDocx'));
    if (uploadType === 'letter' && !campuses.length) {
      return toast.error(t('documentGenerator.toasts.addCampusFirst'));
    }
    if (uploadType === 'letter' && !String(uploadLetterCampusId || '').trim()) {
      return toast.error(t('documentGenerator.toasts.selectCampusForLetter'));
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('template_name', uploadName.trim());
      fd.append('template_type', uploadType);
      if (uploadType === 'letter') {
        fd.append('campus_id', String(uploadLetterCampusId));
      }
      fd.append('file', uploadFile);
      await documentTemplatesAPI.upload(fd);
      toast.success(t('documentGenerator.toasts.templateUploaded'));
      setUploadOpen(false);
      setUploadName('');
      setUploadLetterCampusId('');
      setUploadFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || t('documentGenerator.toasts.uploadFailed'));
    }
    setUploading(false);
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(t('documentGenerator.settings.deleteTemplateConfirm', { name: tpl.template_name }))) return;
    try {
      await documentTemplatesAPI.delete(tpl.template_id);
      toast.success(t('documentGenerator.toasts.deleted'));
      load();
    } catch {
      toast.error(t('documentGenerator.toasts.deleteFailed'));
    }
  };

  const handleReplaceDocx = async () => {
    if (!replaceTpl || !replaceFile) {
      toast.error(t('documentGenerator.toasts.chooseDocx'));
      return;
    }
    setReplacing(true);
    try {
      const fd = new FormData();
      fd.append('file', replaceFile);
      await documentTemplatesAPI.replaceFile(replaceTpl.template_id, fd);
      toast.success(t('documentGenerator.toasts.replaceFileSuccess'));
      setReplaceOpen(false);
      setReplaceTpl(null);
      setReplaceFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || t('documentGenerator.toasts.replaceFileFailed'));
    }
    setReplacing(false);
  };

  const handleToggleStatus = async (tpl) => {
    try {
      await documentTemplatesAPI.update(tpl.template_id, {
        status: tpl.status === 'active' ? 'inactive' : 'active'
      });
      toast.success(t('documentGenerator.toasts.statusUpdated'));
      load();
    } catch {
      toast.error(t('documentGenerator.toasts.updateFailed'));
    }
  };

  if (loading) return <div className="loading-spinner"><div className="spinner"></div></div>;

  return (
    <div>
      {isSuperAdmin && (
        <div
          style={{
            marginBottom: 20,
            padding: 18,
            borderRadius: 12,
            border: '1px solid var(--border)',
            background: 'var(--bg-secondary, #f8fafc)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
                color: '#4f46e5'
              }}
            >
              <FiShield size={20} />
            </div>
            <div>
              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
                {t('documentGenerator.letterTab.title')}
              </h4>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.45 }}>
                {t('documentGenerator.letterTab.subtitle')}
              </p>
            </div>
          </div>
          {letterTabAccessLoading ? (
            <div className="loading-spinner" style={{ minHeight: 80 }}>
              <div className="spinner" />
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gap: 10, marginBottom: letterTabAccess.access_mode === 'specific_users' ? 14 : 16 }}>
                {letterTabAccessOptions.map((opt) => (
                  <label
                    key={opt.value}
                    style={{
                      display: 'flex',
                      gap: 12,
                      alignItems: 'flex-start',
                      padding: 12,
                      borderRadius: 10,
                      border: `2px solid ${letterTabAccess.access_mode === opt.value ? '#4f46e5' : 'var(--border)'}`,
                      background: letterTabAccess.access_mode === opt.value ? '#f5f3ff' : 'var(--white)',
                      cursor: 'pointer'
                    }}
                  >
                    <input
                      type="radio"
                      name="letter-tab-access"
                      checked={letterTabAccess.access_mode === opt.value}
                      onChange={() => setLetterTabAccess((p) => ({ ...p, access_mode: opt.value }))}
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{opt.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4 }}>{opt.hint}</div>
                    </div>
                  </label>
                ))}
              </div>
              {letterTabAccess.access_mode === 'specific_users' && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>
                    {t('documentGenerator.letterTab.selectUsers')}
                  </label>
                  <div
                    style={{
                      maxHeight: 220,
                      overflowY: 'auto',
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      padding: 8,
                      background: 'var(--white)'
                    }}
                  >
                    {allUsersForAccess.length === 0 ? (
                      <p style={{ margin: 8, fontSize: 13, color: 'var(--text-muted)' }}>{t('documentGenerator.letterTab.noActiveUsers')}</p>
                    ) : (
                      allUsersForAccess.map((u) => (
                        <label
                          key={u.user_id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '6px 8px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 13
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={letterTabAccess.specific_user_ids.includes(Number(u.user_id))}
                            onChange={() => toggleSpecificUser(u.user_id)}
                          />
                          <span>
                            {u.name || u.email}{' '}
                            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>({u.email})</span>
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveLetterTabAccess}
                disabled={letterTabSaving}
              >
                {letterTabSaving ? t('common.saving') : t('documentGenerator.letterTab.saveAccess')}
              </button>
            </>
          )}
        </div>
      )}

      <div
        style={{
          marginBottom: 16,
          padding: 14,
          borderRadius: 12,
          background: 'var(--info-light, #eff6ff)',
          border: '1px solid var(--info, #3b82f6)',
          fontSize: 13,
          color: 'var(--text-secondary)',
          lineHeight: 1.5
        }}
      >
        <strong style={{ color: 'var(--text)' }}>{t('documentGenerator.settings.feeRulesTitle')}</strong>
        <p style={{ margin: '8px 0 0' }}>
          {t('documentGenerator.settings.feeRulesBody')}
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0 }}>{t('documentGenerator.settings.documentTemplatesCount', { count: templates.length })}</h3>
        {isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => setUploadOpen(true)}>
            <FiUpload style={{ marginRight: 4 }} /> {t('documentGenerator.settings.uploadTemplate')}
          </button>
        )}
      </div>

      {templates.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
          <FiFileText size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p>{t('documentGenerator.settings.noTemplatesYet')}</p>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>{t('documentGenerator.settings.tableTemplateName')}</th>
                <th>{t('documentGenerator.settings.tableType')}</th>
                <th>{t('documentGenerator.settings.tableCampus')}</th>
                <th style={{ minWidth: 220 }}>{t('documentGenerator.settings.tableLetterAccess')}</th>
                <th>{t('documentGenerator.settings.tableFieldsDetected')}</th>
                <th>{t('documentGenerator.settings.tableStatus')}</th>
                <th>{t('documentGenerator.settings.tableUploaded')}</th>
                {isAdmin && <th style={{ minWidth: 112 }}>{t('common.actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {templates.map(tpl => (
                <tr key={tpl.template_id}>
                  <td style={{ fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <FiFileText style={{ color: '#4f46e5', flexShrink: 0, marginTop: 2 }} />
                      <div style={{ minWidth: 0 }}>
                        <div>{tpl.template_name}</div>
                        {tpl.original_filename ? (
                          <div
                            style={{
                              fontSize: 11,
                              color: 'var(--text-muted)',
                              fontWeight: 400,
                              marginTop: 2,
                              maxWidth: 260,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={tpl.original_filename}
                          >
                            {tpl.original_filename}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td>
                    {(() => {
                      const typeBadges = {
                        agreement: {
                          cls: 'badge-blue',
                          label: t('documentGenerator.settings.agreementBadge'),
                        },
                        payment_receipt: {
                          cls: 'badge-purple',
                          label: t('documentGenerator.settings.paymentReceiptBadge'),
                        },
                        letter: {
                          cls: 'badge-green',
                          label: t('documentGenerator.settings.letterBadge'),
                        },
                      };
                      const b = typeBadges[tpl.template_type] || typeBadges.letter;
                      return <span className={`badge ${b.cls}`}>{b.label}</span>;
                    })()}
                  </td>
                  <td style={{ fontSize: 13, minWidth: 140 }}>
                    {tpl.template_type !== 'letter' ? (
                      <span style={{ color: 'var(--text-muted)' }}>—</span>
                    ) : isAdmin ? (
                      <select
                        className="form-control"
                        style={{ fontSize: 12, padding: '4px 8px' }}
                        value={tpl.campus_id != null ? String(tpl.campus_id) : ''}
                        onChange={async (e) => {
                          const v = e.target.value;
                          try {
                            await documentTemplatesAPI.update(tpl.template_id, {
                              campus_id: v === '' ? null : Number(v)
                            });
                            toast.success(t('documentGenerator.toasts.campusUpdated'));
                            load();
                          } catch {
                            toast.error(t('documentGenerator.toasts.updateFailed'));
                          }
                        }}
                      >
                        <option value="">{t('documentGenerator.settings.allCampusesGeneric')}</option>
                        {campuses.map((c) => (
                          <option key={c.campus_id} value={c.campus_id}>
                            {c.campus_name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span>
                        {tpl.template_campus_name ||
                          (tpl.campus_id
                            ? t('documentGenerator.settings.campusNumber', { num: tpl.campus_id })
                            : t('documentGenerator.settings.allCampuses'))}
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, verticalAlign: 'middle' }}>
                    {tpl.template_type !== 'letter' ? (
                      <span style={{ color: 'var(--text-muted)' }}>—</span>
                    ) : isAdmin ? (
                      <select
                        className="form-control"
                        style={{ fontSize: 12, padding: '4px 8px', maxWidth: 280 }}
                        value={tpl.letter_fee_gate || 'none'}
                        onChange={async (e) => {
                          const v = e.target.value;
                          try {
                            await documentTemplatesAPI.update(tpl.template_id, { letter_fee_gate: v });
                            toast.success(t('documentGenerator.toasts.letterAccessUpdated'));
                            load();
                          } catch {
                            toast.error(t('documentGenerator.toasts.updateFailed'));
                          }
                        }}
                      >
                        {letterFeeGateOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>
                        {(letterFeeGateOptions.find((o) => o.value === (tpl.letter_fee_gate || 'none')) || letterFeeGateOptions[0])
                          .label}
                      </span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(tpl.fields || []).map(f => (
                        <span key={f.field_id} style={{
                          padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500,
                          background: f.auto_map ? '#dcfce7' : '#fef3c7',
                          color: f.auto_map ? '#166534' : '#92400e'
                        }}>
                          {f.field_label}
                          {f.auto_map && <FiCheck size={10} style={{ marginLeft: 3 }} />}
                        </span>
                      ))}
                      {(!tpl.fields || tpl.fields.length === 0) && (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('documentGenerator.settings.noFields')}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span
                      className={`badge ${tpl.status === 'active' ? 'badge-green' : 'badge-gray'}`}
                      style={{ cursor: isAdmin ? 'pointer' : 'default' }}
                      onClick={() => isAdmin && handleToggleStatus(tpl)}
                      title={isAdmin ? t('documentGenerator.settings.clickToToggle') : ''}
                    >
                      {tpl.status}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {tpl.created_at ? new Date(tpl.created_at).toLocaleDateString() : '-'}
                  </td>
                  {isAdmin && (
                    <td>
                      <div className="action-buttons">
                        <button
                          type="button"
                          className="edit"
                          onClick={() => {
                            setReplaceTpl(tpl);
                            setReplaceFile(null);
                            setReplaceOpen(true);
                          }}
                          title={t('documentGenerator.settings.replaceDocxTitle')}
                        >
                          <FiEdit />
                        </button>
                        <button
                          type="button"
                          className="delete"
                          onClick={() => handleDelete(tpl)}
                          title={t('documentGenerator.settings.deleteTemplateTitle')}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Modal */}
      <Modal isOpen={uploadOpen} onClose={() => setUploadOpen(false)} title={t('documentGenerator.settings.uploadModalTitle')}>
        <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
          <div className="form-group">
            <label>
              {t('documentGenerator.settings.templateNameLabel')} <span className="required">*</span>
            </label>
            <input
              type="text"
              placeholder={t('documentGenerator.settings.templateNamePlaceholder')}
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>
              {t('documentGenerator.settings.templateTypeLabel')} <span className="required">*</span>
            </label>
            <select
              value={uploadType}
              onChange={(e) => {
                setUploadType(e.target.value);
                if (e.target.value !== 'letter') setUploadLetterCampusId('');
              }}
            >
              <option value="letter">{t('documentGenerator.settings.typeLetterOption')}</option>
              <option value="agreement">{t('documentGenerator.settings.typeAgreementOption')}</option>
              <option value="payment_receipt">
                {t('documentGenerator.settings.typePaymentReceiptOption')}
              </option>
            </select>
          </div>
          {uploadType === 'letter' && (
            <div className="form-group">
              <label>
                {t('documentGenerator.settings.campusForLetterLabel')} <span className="required">*</span>
              </label>
              <select
                value={uploadLetterCampusId}
                onChange={(e) => setUploadLetterCampusId(e.target.value)}
                disabled={campuses.length === 0}
              >
                <option value="">
                  {campuses.length
                    ? t('documentGenerator.settings.selectCampusPlaceholder')
                    : t('documentGenerator.settings.noCampusesConfiguredPlaceholder')}
                </option>
                {campuses.map((c) => (
                  <option key={c.campus_id} value={c.campus_id}>
                    {c.campus_name}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, marginBottom: 0 }}>
                {t('documentGenerator.settings.campusLetterHint')}
              </p>
            </div>
          )}
          <div className="form-group">
            <label>
              {t('documentGenerator.settings.docxFileLabel')} <span className="required">*</span>
            </label>
            <div style={{
              border: '2px dashed var(--border)', borderRadius: 12, padding: 24, textAlign: 'center',
              cursor: 'pointer', background: uploadFile ? '#f0fdf4' : '#fafafa',
              transition: 'all 0.2s'
            }} onClick={() => document.getElementById('tpl-file-input').click()}>
              <input id="tpl-file-input" type="file" accept=".docx" style={{ display: 'none' }}
                onChange={e => setUploadFile(e.target.files[0] || null)} />
              {uploadFile ? (
                <div>
                  <FiCheck size={24} style={{ color: '#16a34a', marginBottom: 4 }} />
                  <p style={{ margin: 0, fontWeight: 600, color: '#166534' }}>{uploadFile.name}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                    {t('documentGenerator.settings.clickToChangeFile')}
                  </p>
                </div>
              ) : (
                <div>
                  <FiUpload size={24} style={{ color: 'var(--text-muted)', marginBottom: 4 }} />
                  <p style={{ margin: 0, fontWeight: 500 }}>{t('documentGenerator.settings.clickToSelectDocx')}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                    {t('documentGenerator.settings.docxPlaceholderHelp', { fieldSyntax: '{{Field_Name}}' })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={() => setUploadOpen(false)}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" onClick={handleUpload} disabled={uploading}>
            {uploading ? t('common.uploading') : t('documentGenerator.settings.uploadAndDetectFields')}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={replaceOpen}
        onClose={() => {
          if (replacing) return;
          setReplaceOpen(false);
          setReplaceTpl(null);
          setReplaceFile(null);
        }}
        title={
          replaceTpl
            ? t('documentGenerator.settings.replaceModalTitle', { name: replaceTpl.template_name })
            : t('documentGenerator.settings.replaceModalTitleFallback')
        }
      >
        {replaceTpl && (
          <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
              {t('documentGenerator.settings.replaceIntro')}
            </p>
            <div className="form-group" style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {t('documentGenerator.settings.currentFileLabel')}
              </label>
              <div style={{ fontSize: 13, wordBreak: 'break-all' }}>{replaceTpl.original_filename || '—'}</div>
            </div>
            <div className="form-group">
              <label>
                {t('documentGenerator.settings.newDocxLabel')} <span className="required">*</span>
              </label>
              <input
                type="file"
                accept=".docx"
                onChange={(e) => setReplaceFile(e.target.files?.[0] || null)}
              />
              {replaceFile && (
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8, marginBottom: 0 }}>
                  {t('documentGenerator.settings.selectedFile', { name: replaceFile.name })}
                </p>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setReplaceOpen(false);
                  setReplaceTpl(null);
                  setReplaceFile(null);
                }}
                disabled={replacing}
              >
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-primary" onClick={handleReplaceDocx} disabled={replacing || !replaceFile}>
                {replacing ? t('documentGenerator.settings.replacing') : t('documentGenerator.settings.replaceFile')}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

/** Date fields: show dd-mm-yyyy text + native calendar picker; store yyyy-mm-dd for the API. */
function TemplateDateField({ value, onChange, inputStyle }) {
  const { t } = useTranslation();
  const [text, setText] = useState(() => isoDateToDdMmYyyy(value));
  useEffect(() => {
    setText(isoDateToDdMmYyyy(value) || '');
  }, [value]);

  const applyIso = (iso) => {
    onChange(iso || '');
  };

  const onTextBlur = () => {
    const trimmed = text.trim();
    if (!trimmed) {
      applyIso('');
      return;
    }
    const iso = ddMmYyyyToIso(trimmed);
    if (iso) applyIso(iso);
    else setText(isoDateToDdMmYyyy(value) || '');
  };

  const isoForPicker = value && /^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? value : '';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={t('documentGenerator.templateDate.placeholder')}
        title={t('documentGenerator.templateDate.textTitle')}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={onTextBlur}
        style={{ ...inputStyle, width: 132, fontVariantNumeric: 'tabular-nums' }}
      />
      <input
        type="date"
        aria-label={t('documentGenerator.templateDate.calendarAria')}
        title={t('documentGenerator.templateDate.calendarTitle')}
        value={isoForPicker}
        onChange={(e) => {
          const iso = e.target.value;
          applyIso(iso);
          setText(isoDateToDdMmYyyy(iso));
        }}
        style={{ ...inputStyle, width: 'auto', minWidth: 148, cursor: 'pointer' }}
      />
    </div>
  );
}

// ─── ENTITY DOCUMENT GENERATOR (Lead or Student page) ───
export const EntityDocumentGenerator = forwardRef(function EntityDocumentGenerator(
  {
    entityType,
    entityId,
    entityLabel,
    campusId,
    hasVerifiedApplicationFee = false,
    hasVerifiedTuitionFee = false,
    /**
     * Optional override for the template type to query. When omitted, defaults
     * to 'agreement' for leads and 'letter' for students. Passing
     * 'payment_receipt' lets the caller reuse this component to surface the
     * payment-receipt templates in the student Fees tab.
     */
    templateType,
  },
  ref
) {
  const { user } = useAuth();
  const letterFeeGateBypass = String(user?.role || '').toLowerCase() === 'super_admin';
  const [templates, setTemplates] = useState([]);
  const [generated, setGenerated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [autoFillFields, setAutoFillFields] = useState([]);
  const [generating, setGenerating] = useState(false);
  // Payment-receipt only: the student fee bucket this receipt is issued
  // against. Governs Code / Description / Unit_Price / Advance_Payment …
  const [selectedFeeType, setSelectedFeeType] = useState('application_fee');
  // Payment-receipt only: the specific student_fees row this receipt is for
  // (when launched from a per-row "Generate Receipt" button). Passed to the
  // backend so Advance / Net_Pay / Balance are computed around this row.
  const [selectedFeeId, setSelectedFeeId] = useState(null);
  // Metadata about the source fee row (shown in the modal as context).
  const [sourceFeeMeta, setSourceFeeMeta] = useState(null);
  const [feeTypeSwitching, setFeeTypeSwitching] = useState(false);
  // Tiny picker shown when openForFeeRow is called but there are multiple
  // active payment_receipt templates and no default to choose automatically.
  const [feeRowPicker, setFeeRowPicker] = useState(null);
  const { t } = useTranslation();

  const type = templateType || (entityType === 'lead' ? 'agreement' : 'letter');
  const typeLabel =
    type === 'agreement'
      ? t('documentGenerator.typeAgreement')
      : type === 'payment_receipt'
        ? t('documentGenerator.typePaymentReceipt')
        : t('documentGenerator.typeLetter');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const listParams = { type, status: 'active' };
      if (entityType === 'student' && type === 'letter') {
        const cid = campusId != null && campusId !== '' ? Number(campusId) : NaN;
        listParams.letter_campus_id = !Number.isNaN(cid) && cid > 0 ? String(cid) : 'none';
      }
      const [tplRes, genRes] = await Promise.all([
        documentTemplatesAPI.list(listParams),
        documentTemplatesAPI.getGenerated({ entity_type: entityType, entity_id: entityId })
      ]);
      setTemplates(tplRes.data);
      // Filter the generated list to match the template type we're rendering
      // so the Fees tab only lists payment receipts and the Letters tab only
      // lists letters / agreements.
      setGenerated((genRes.data || []).filter((d) => !d.template_type || d.template_type === type));
    } catch {
      toast.error(t('documentGenerator.toasts.loadFailed'));
    }
    setLoading(false);
  }, [type, entityType, entityId, campusId, t]);

  useEffect(() => { load(); }, [load]);

  const letterTemplatesVisible = useMemo(() => {
    if (entityType !== 'student' || type !== 'letter') return templates;
    return templates.filter((tpl) => {
      if (letterFeeGateBypass) return true;
      const g = String(tpl.letter_fee_gate || 'none').toLowerCase();
      if (!g || g === 'none') return true;
      if (g === 'application_fee') return Boolean(hasVerifiedApplicationFee);
      if (g === 'tuition_fee') return Boolean(hasVerifiedTuitionFee);
      return true;
    });
  }, [
    templates,
    entityType,
    type,
    letterFeeGateBypass,
    hasVerifiedApplicationFee,
    hasVerifiedTuitionFee
  ]);

  /**
   * Fetch server-computed auto-fill values for a template. For
   * payment_receipt templates we pass along the chosen fee_type (and
   * optionally fee_id, when the receipt is scoped to a specific fee row)
   * so the backend can compute the right Code / Description / Unit_Price /
   * Advance_Payment / Balance_Pay values.
   */
  const fetchAutoFill = useCallback(
    async (tpl, feeType, feeId) => {
      const params = {
        template_id: tpl.template_id,
        entity_type: entityType,
        entity_id: entityId
      };
      if (type === 'payment_receipt') {
        if (feeType) params.fee_type = feeType;
        if (feeId != null && feeId !== '') params.fee_id = feeId;
      }
      try {
        const res = await documentTemplatesAPI.getAutoFill(params);
        const fields = res.data;
        setAutoFillFields(fields);
        const vals = {};
        fields.forEach((f) => {
          const raw = f.value || '';
          vals[f.field_key] =
            getTemplateFieldInputKind(f.field_key) === 'date'
              ? normalizeTemplateDateValueForIso(raw)
              : raw;
        });
        setFieldValues(vals);
      } catch {
        const vals = {};
        (tpl.fields || []).forEach((f) => {
          vals[f.field_key] = '';
        });
        setAutoFillFields(
          (tpl.fields || []).map((f) => ({
            field_key: f.field_key,
            field_label: f.field_label,
            auto_map: f.auto_map,
            value: ''
          }))
        );
        setFieldValues(vals);
      }
    },
    [entityType, entityId, type]
  );

  /**
   * Open the Generate modal for a given template. For payment receipts an
   * optional `feeContext` ({ fee_id, fee_type, meta }) scopes the receipt
   * to a specific student_fees row, so that Advance / Net_Pay / Balance are
   * computed around that row.
   */
  const openGenerate = async (tpl, feeContext) => {
    setSelectedTemplate(tpl);
    setFieldValues({});
    setAutoFillFields([]);
    let feeType;
    let feeId = null;
    let meta = null;
    if (type === 'payment_receipt') {
      feeType = feeContext?.fee_type || 'application_fee';
      feeId = feeContext?.fee_id != null ? feeContext.fee_id : null;
      meta = feeContext?.meta || null;
      setSelectedFeeType(feeType);
      setSelectedFeeId(feeId);
      setSourceFeeMeta(meta);
    } else {
      setSelectedFeeId(null);
      setSourceFeeMeta(null);
    }
    setGenerateOpen(true);
    await fetchAutoFill(tpl, type === 'payment_receipt' ? feeType : undefined, feeId);
  };

  const handleFeeTypeChange = async (nextFeeType) => {
    setSelectedFeeType(nextFeeType);
    if (!selectedTemplate) return;
    setFeeTypeSwitching(true);
    try {
      await fetchAutoFill(selectedTemplate, nextFeeType, selectedFeeId);
    } finally {
      setFeeTypeSwitching(false);
    }
  };

  /**
   * Imperative entrypoint called by the Fees tab when the user clicks
   * "Generate Receipt" on a specific fee row. Picks the right template
   * automatically when possible, else shows a small template picker.
   */
  useImperativeHandle(ref, () => ({
    openForFeeRow: async ({ fee_id, fee_type, meta } = {}) => {
      if (type !== 'payment_receipt') return;
      let activeTemplates = templates.filter((tpl) => String(tpl.status || 'active') === 'active');
      if (!activeTemplates.length) {
        try {
          const res = await documentTemplatesAPI.list({ type: 'payment_receipt', status: 'active' });
          activeTemplates = (res.data || []).filter((tpl) => String(tpl.status || 'active') === 'active');
          setTemplates((prev) => (prev.length ? prev : res.data || []));
        } catch {
          /* fall through and handle as empty below */
        }
      }
      if (!activeTemplates.length) {
        toast.info(t('documentGenerator.paymentReceipt.noTemplateUploaded'));
        return;
      }
      const feeContext = { fee_id, fee_type, meta };
      if (activeTemplates.length === 1) {
        openGenerate(activeTemplates[0], feeContext);
      } else {
        setFeeRowPicker({ templates: activeTemplates, feeContext });
      }
    }
  }));

  const triggerBlobDownload = async (docId) => {
    try {
      const res = await documentTemplatesAPI.download(docId);
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      let filename = match ? match[1].trim() : '';
      if (!filename) {
        const ct = String(res.headers['content-type'] || '');
        const ext = ct.includes('pdf') ? '.pdf' : ct.includes('wordprocessingml') ? '.docx' : '.docx';
        filename = `document-${docId}${ext}`;
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('documentGenerator.toasts.downloadFailed'));
    }
  };

  const handleGenerate = async () => {
    if (type === 'payment_receipt' && !selectedFeeType) {
      toast.error(t('documentGenerator.paymentReceipt.feeTypeRequired'));
      return;
    }
    setGenerating(true);
    try {
      const payload = {
        template_id: selectedTemplate.template_id,
        entity_type: entityType,
        entity_id: entityId,
        field_values: fieldValues
      };
      if (type === 'payment_receipt') {
        payload.fee_type = selectedFeeType;
        if (selectedFeeId != null) payload.fee_id = selectedFeeId;
      }
      const res = await documentTemplatesAPI.generate(payload);
      toast.success(
        res.data?.format === 'pdf'
          ? t('documentGenerator.settings.generatedPdf')
          : t('documentGenerator.settings.generatedDocx')
      );
      setGenerateOpen(false);
      load();
      triggerBlobDownload(res.data.doc_id);
    } catch (err) {
      toast.error(err.response?.data?.message || t('documentGenerator.toasts.generationFailed'));
    }
    setGenerating(false);
  };

  const handleDownload = (docId) => {
    triggerBlobDownload(docId);
  };

  const handleDeleteGenerated = async (doc) => {
    if (!window.confirm(t('documentGenerator.settings.deleteGeneratedConfirm'))) return;
    try {
      await documentTemplatesAPI.deleteGenerated(doc.doc_id);
      toast.success(t('documentGenerator.toasts.deleted'));
      load();
    } catch {
      toast.error(t('documentGenerator.toasts.deleteFailed'));
    }
  };

  // ─── Signed letter handlers ───
  const [signedUploading, setSignedUploading] = useState(null);

  const handleUploadSigned = async (docId, file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx', 'jpg', 'jpeg', 'png'].includes(ext)) {
      toast.error(t('documentGenerator.toasts.allowedFileTypes'));
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error(t('documentGenerator.toasts.maxFileSize'));
      return;
    }
    setSignedUploading(docId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await documentTemplatesAPI.uploadSigned(docId, fd);
      toast.success(t('documentGenerator.signedUploadSuccess'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || t('documentGenerator.signedUploadFailed'));
    } finally {
      setSignedUploading(null);
    }
  };

  const handleViewSigned = async (docId) => {
    try {
      const res = await documentTemplatesAPI.getSignedUrl(docId);
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error(t('documentGenerator.viewFailed'));
    }
  };

  const handleDeleteSigned = async (docId) => {
    if (!window.confirm(t('documentGenerator.signedDeleteConfirm'))) return;
    try {
      await documentTemplatesAPI.deleteSigned(docId);
      toast.success(t('documentGenerator.signedDeleteSuccess'));
      load();
    } catch { toast.error(t('documentGenerator.signedDeleteFailed')); }
  };

  if (loading) return <div className="loading-spinner"><div className="spinner"></div></div>;

  return (
    <div>
      {/* Available Templates */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ marginBottom: 8, color: 'var(--text)' }}>
          {t('documentGenerator.entity.availableTemplates', { type: typeLabel })}
        </h4>
        {entityType === 'student' && type === 'letter' && (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
            <p style={{ margin: '0 0 8px' }}>
              {campusId != null && campusId !== '' && !Number.isNaN(Number(campusId))
                ? t('documentGenerator.entity.campusHelpWithCampus')
                : t('documentGenerator.entity.campusHelpNoCampus')}
            </p>
            {!letterFeeGateBypass && (
              <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                {t('documentGenerator.entity.feeGateHelp')}
              </p>
            )}
          </div>
        )}
        {templates.length === 0 ? (
          <div style={{
            padding: 24, textAlign: 'center', color: 'var(--text-muted)',
            border: '1px dashed var(--border)', borderRadius: 12
          }}>
            <FiFileText size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
            <p style={{ margin: 0 }}>
              {entityType === 'student' && type === 'letter'
                ? t('documentGenerator.entity.noLetterTemplatesCampus')
                : t('documentGenerator.entity.noTemplatesAdmin', { type: typeLabel })}
            </p>
          </div>
        ) : letterTemplatesVisible.length === 0 ? (
          <div style={{
            padding: 24, textAlign: 'center', color: 'var(--text-muted)',
            border: '1px dashed var(--border)', borderRadius: 12
          }}>
            <FiFileText size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
            <p style={{ margin: 0 }}>{t('documentGenerator.entity.noLettersFeeRules')}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {letterTemplatesVisible.map(tpl => {
              const signedDoc = generated.find(d => d.template_id === tpl.template_id && d.signed_file_name);
              const latestDoc = generated.find(d => d.template_id === tpl.template_id);
              return (
              <div key={tpl.template_id} style={{
                border: '1px solid var(--border)', borderRadius: 12, padding: 16,
                background: 'var(--white)',
                transition: 'all 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#4f46e5'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(79,70,229,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#4f46e5', flexShrink: 0
                  }}>
                    <FiFileText size={18} />
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{tpl.template_name}</div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                  {(tpl.fields || []).slice(0, 5).map(f => (
                    <span key={f.field_id} style={{
                      padding: '1px 6px', borderRadius: 4, fontSize: 10,
                      background: '#f1f5f9', color: '#475569'
                    }}>
                      {f.field_label}
                    </span>
                  ))}
                  {(tpl.fields || []).length > 5 && (
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                      {t('documentGenerator.entity.moreFields', { count: (tpl.fields || []).length - 5 })}
                    </span>
                  )}
                </div>
                <button className="btn btn-primary btn-sm" style={{ width: '100%', marginBottom: 8 }}
                  onClick={() => openGenerate(tpl)}>
                  <FiEdit style={{ marginRight: 4 }} /> {t('documentGenerator.generateBtn', { type: typeLabel })}
                </button>

                {/* Signed letter per template — not relevant for payment receipts */}
                {type === 'payment_receipt' ? null : signedDoc ? (
                  <div style={{
                    padding: '8px 10px', borderRadius: 8, background: '#f0fdf4',
                    border: '1px solid #bbf7d0', fontSize: 12
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <FiCheck size={13} style={{ color: '#16a34a' }} />
                      <span style={{ fontWeight: 600, color: '#166534' }}>{t('documentGenerator.signedUploaded')}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, wordBreak: 'break-all' }}>
                      {signedDoc.signed_file_name}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="btn btn-outline btn-sm" style={{ padding: '2px 8px', fontSize: 11 }}
                        onClick={() => handleViewSigned(signedDoc.doc_id)}>
                        <FiEye size={11} /> {t('documentGenerator.viewSigned')}
                      </button>
                      <label className="btn btn-outline btn-sm" style={{ padding: '2px 8px', fontSize: 11, cursor: 'pointer', margin: 0 }}>
                        <FiEdit size={11} /> {t('documentGenerator.replaceSigned')}
                        <input type="file" accept=".pdf,.docx,.jpg,.jpeg,.png" style={{ display: 'none' }}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleUploadSigned(signedDoc.doc_id, f);
                            e.target.value = '';
                          }}
                        />
                      </label>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        style={{ padding: '2px 8px', fontSize: 11, color: 'var(--danger)', borderColor: 'var(--danger)' }}
                        title={t('documentGenerator.deleteSigned')}
                        aria-label={t('documentGenerator.deleteSigned')}
                        onClick={() => handleDeleteSigned(signedDoc.doc_id)}
                      >
                        <FiTrash2 size={11} />
                      </button>
                    </div>
                  </div>
                ) : latestDoc ? (
                  <label style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '6px 10px', borderRadius: 8, border: '1px dashed var(--border)',
                    background: '#fafafa', cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)',
                    transition: 'border-color 0.15s'
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#4f46e5'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                  >
                    {signedUploading === latestDoc.doc_id
                      ? t('documentGenerator.entity.uploading')
                      : <><FiUpload size={13} /> {t('documentGenerator.uploadSigned', { type: typeLabel })}</>}
                    <input type="file" accept=".pdf,.docx,.jpg,.jpeg,.png" style={{ display: 'none' }}
                      disabled={signedUploading === latestDoc.doc_id}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUploadSigned(latestDoc.doc_id, f);
                        e.target.value = '';
                      }}
                    />
                  </label>
                ) : null}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Generated Documents */}
      <div>
        <h4 style={{ marginBottom: 12, color: 'var(--text)' }}>
          {t('documentGenerator.generatedDocuments')}
          {generated.length > 0 ? ` (${generated.length})` : ''}
        </h4>
        {generated.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>{t('documentGenerator.noDocuments')}</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>{t('documentGenerator.tableDocType')}</th>
                  <th>{t('documentGenerator.tableGeneratedBy')}</th>
                  <th>{t('documentGenerator.tableDate')}</th>
                  <th style={{ width: 120 }}>{t('documentGenerator.tableActions')}</th>
                </tr>
              </thead>
              <tbody>
                {generated.map(doc => (
                  <tr key={doc.doc_id}>
                    <td style={{ fontWeight: 600 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FiFileText style={{ color: '#4f46e5', flexShrink: 0 }} />
                        {doc.template_name}
                      </div>
                    </td>
                    <td>{doc.generated_by_name || '-'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {doc.created_at ? new Date(doc.created_at).toLocaleString() : '-'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="edit"
                          onClick={() => handleDownload(doc.doc_id)}
                          title={t('documentGenerator.entity.downloadGeneratedTooltip')}
                        >
                          <FiDownload />
                        </button>
                        <button
                          className="delete"
                          onClick={() => handleDeleteGenerated(doc)}
                          title={t('documentGenerator.entity.deleteTooltip')}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Generate Modal */}
      <Modal
        isOpen={generateOpen}
        onClose={() => setGenerateOpen(false)}
        title={t('documentGenerator.entity.generateModalTitle', {
          type: typeLabel,
          name: selectedTemplate?.template_name || ''
        })}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 16px' }}>
            {t('documentGenerator.entity.generateIntro', { entity: entityLabel || entityType })}
          </p>

          {/* Payment-receipt only: when launched from a specific fee row,
              surface which payment this receipt is being issued for so the
              user has clear context for the numbers below. */}
          {type === 'payment_receipt' && sourceFeeMeta && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '10px 12px',
                marginBottom: 14,
                background: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: 8
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  borderRadius: 8,
                  background: '#0ea5e9',
                  color: '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FiDollarSign size={15} />
              </span>
              <div style={{ fontSize: 13, color: '#075985', lineHeight: 1.45 }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>
                  {t('documentGenerator.paymentReceipt.sourceRowHeading')}
                </div>
                <div>
                  {t('documentGenerator.paymentReceipt.sourceRowSummary', {
                    amount: sourceFeeMeta.amount || '-',
                    date: sourceFeeMeta.date || '-',
                    method: sourceFeeMeta.method || '-'
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Payment-receipt only: pick which fee this receipt is for. Changing
              the selection re-fetches the auto-filled Code / Description /
              Unit Price / Advance / Balance values from the server. */}
          {type === 'payment_receipt' && (
            <div style={{ marginBottom: 18 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 10,
                  gap: 12,
                  flexWrap: 'wrap'
                }}
              >
                <label
                  style={{
                    margin: 0,
                    fontWeight: 700,
                    fontSize: 13,
                    color: 'var(--text)',
                    textTransform: 'uppercase',
                    letterSpacing: 0.3
                  }}
                >
                  {t('documentGenerator.paymentReceipt.feeTypeLabel')}{' '}
                  <span className="required">*</span>
                </label>
                {feeTypeSwitching && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#4338ca',
                      background: '#eef2ff',
                      padding: '2px 8px',
                      borderRadius: 999
                    }}
                  >
                    {t('common.loading')}
                  </span>
                )}
              </div>

              <div
                role="radiogroup"
                aria-label={t('documentGenerator.paymentReceipt.feeTypeLabel')}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 10
                }}
              >
                {[
                  {
                    value: 'application_fee',
                    label: t('documentGenerator.paymentReceipt.applicationFeeOption'),
                    code: 'GSFAF01',
                    Icon: FiClipboard,
                    accent: '#4f46e5',
                    tint: '#eef2ff'
                  },
                  {
                    value: 'tuition_fee',
                    label: t('documentGenerator.paymentReceipt.tuitionFeeOption'),
                    code: 'GSFTF02',
                    Icon: FiBookOpen,
                    accent: '#0891b2',
                    tint: '#ecfeff'
                  }
                ].map(({ value, label, code, Icon, accent, tint }) => {
                  const active = selectedFeeType === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      disabled={feeTypeSwitching}
                      onClick={() => {
                        if (!active) handleFeeTypeChange(value);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 14px',
                        borderRadius: 10,
                        border: `1.5px solid ${active ? accent : 'var(--border)'}`,
                        background: active ? tint : 'var(--white)',
                        cursor: feeTypeSwitching ? 'wait' : 'pointer',
                        opacity: feeTypeSwitching && !active ? 0.6 : 1,
                        textAlign: 'left',
                        transition: 'all 0.15s ease',
                        boxShadow: active ? `0 0 0 3px ${accent}22` : 'none',
                        outline: 'none',
                        width: '100%'
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: active ? accent : '#f1f5f9',
                          color: active ? '#fff' : '#475569',
                          flexShrink: 0,
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <Icon size={18} />
                      </span>
                      <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: active ? accent : 'var(--text)',
                            lineHeight: 1.2
                          }}
                        >
                          {label}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                            marginTop: 3,
                            letterSpacing: 0.4
                          }}
                        >
                          {code}
                        </span>
                      </span>
                      <span
                        aria-hidden
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `2px solid ${active ? accent : 'var(--border)'}`,
                          background: active ? accent : 'transparent',
                          color: '#fff',
                          flexShrink: 0,
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {active && <FiCheck size={12} strokeWidth={3} />}
                      </span>
                    </button>
                  );
                })}
              </div>

              <p
                style={{
                  margin: '10px 0 0',
                  fontSize: 12,
                  color: 'var(--text-muted)',
                  lineHeight: 1.45
                }}
              >
                {t('documentGenerator.paymentReceipt.feeTypeHelp')}
              </p>
            </div>
          )}

          <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
            {autoFillFields.map(f => {
              const kind = getTemplateFieldInputKind(f.field_key);
              const inputStyle = { color: 'var(--text)', background: 'var(--white)' };
              return (
              <div className="form-group" key={f.field_key}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text)' }}>
                  {f.field_label}
                  {f.auto_map && f.value && (
                    <span style={{
                      fontSize: 10, padding: '1px 6px', borderRadius: 4,
                      background: '#dcfce7', color: '#166534'
                    }}>{t('documentGenerator.entity.autoFilledBadge')}</span>
                  )}
                </label>
                {kind === 'date' ? (
                  <TemplateDateField
                    value={fieldValues[f.field_key] || ''}
                    onChange={(iso) =>
                      setFieldValues((prev) => ({ ...prev, [f.field_key]: iso }))
                    }
                    inputStyle={inputStyle}
                  />
                ) : kind === 'textarea' ? (
                  <textarea
                    value={fieldValues[f.field_key] || ''}
                    onChange={e => setFieldValues(prev => ({ ...prev, [f.field_key]: e.target.value }))}
                    placeholder={t('documentGenerator.entity.enterField', { label: f.field_label })}
                    rows={4}
                    style={{ ...inputStyle, minHeight: 88, resize: 'vertical' }}
                  />
                ) : (
                  <input
                    type="text"
                    value={fieldValues[f.field_key] || ''}
                    onChange={e => setFieldValues(prev => ({ ...prev, [f.field_key]: e.target.value }))}
                    placeholder={t('documentGenerator.entity.enterField', { label: f.field_label })}
                    style={inputStyle}
                  />
                )}
              </div>
              );
            })}
            {autoFillFields.length === 0 && (
              <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 16 }}>
                {t('documentGenerator.entity.noDynamicFields')}
              </p>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => setGenerateOpen(false)}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" onClick={handleGenerate} disabled={generating}>
            <FiDownload style={{ marginRight: 4 }} />
            {generating ? t('documentGenerator.entity.generating') : t('documentGenerator.entity.generateAndDownload')}
          </button>
        </div>
      </Modal>

      {/* Template picker shown from openForFeeRow when multiple Payment
          Receipt templates exist and we can't default safely. */}
      {feeRowPicker && (
        <Modal
          isOpen={!!feeRowPicker}
          onClose={() => setFeeRowPicker(null)}
          title={t('documentGenerator.paymentReceipt.chooseTemplateTitle')}
          size="md"
        >
          <p style={{ color: 'var(--text-muted)', marginTop: 0, marginBottom: 12, fontSize: 13 }}>
            {t('documentGenerator.paymentReceipt.chooseTemplateHelp')}
          </p>
          <div style={{ display: 'grid', gap: 8 }}>
            {feeRowPicker.templates.map((tpl) => (
              <button
                key={tpl.template_id}
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  const ctx = feeRowPicker.feeContext;
                  setFeeRowPicker(null);
                  openGenerate(tpl, ctx);
                }}
                style={{ justifyContent: 'flex-start', textAlign: 'left' }}
              >
                <FiFileText style={{ marginRight: 8 }} />
                {tpl.name}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setFeeRowPicker(null)}>
              {t('common.cancel')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
});
